import affirm from './Affirm'

/**
 * Core Algorithms that are shared between different project/problems.
 * This file needs to be as lean as possible and must not include any external dependencies.
 */
const locale = 'en-US'
const formatter = new Intl.NumberFormat(locale)
const algo = {
    number: (num: number) => (!algo.hasVal(num) || isNaN(num)) ? ' ' : formatter.format(num),
    hasVal: (value: any) => value !== null && value !== undefined,
    sleep: (milliseconds: number) => {
        affirm(milliseconds > 0, 'Invalid @milliseconds parameter. Must be a positive number.')

        return new Promise(resolve => setTimeout(resolve, milliseconds))
    },
    rng: (min: number, max: number) => {
        affirm(min, 'Invalid @min parameter. Must be a valid number.')
        affirm(max, 'Invalid @max parameter. Must be a valid number.')
        affirm(min < max, 'The minimum value must be less than the maximum value.')

        return Math.floor(Math.random() * (max - min + 1)) + min
    },
    uniqBy: <T, K extends keyof T>(array: T[], pk: K): T[K][] => {
        if (!array || array.length === 0) return []
        return Array.from(new Set(array.map(x => x[pk])))
    },
    uniq: <T>(array: T[]): T[] => {
        if (!array || array.length === 0) return []
        return Array.from(new Set(array))
    },
    duplicates: <T, K extends keyof T>(array: T[], pk: K): T[] => {
        if (!array || array.length === 0) return []
        return array.filter(x => array.filter(k => k[pk] === x[pk]).length > 1)
    },
    locations: (string: string, substring: string) => {
        affirm(string, 'Invalid @string parameter.')
        affirm(substring, 'Invalid @substring parameter.')

        const a: number[] = []
        let i = -1
        while ((i = string.indexOf(substring, i + 1)) >= 0) a.push(i)
        return a
    },
    isNumber: (value: any) => typeof value === 'number',
    orderBy: <T>(list: T[], key?: keyof T | ((x: T) => any), direction: 'asc' | 'desc' = 'asc'): T[] => {
        affirm(list, 'Invalid array.')
        affirm(direction, 'Invalid direction parameter. Must be "asc" or "desc".')

        if (list.length === 0) return []

        const sortedList = [...list]

        if (key) {
            if (typeof (key) === 'function') {
                sortedList.sort((a, b) => {
                    const aValue = key(a)
                    const bValue = key(b)

                    if (aValue < bValue) return direction === 'asc' ? -1 : 1
                    if (aValue > bValue) return direction === 'asc' ? 1 : -1
                    return 0
                })
                return sortedList
            } else {
                sortedList.sort((a, b) => {
                    const aValue = a[key]
                    const bValue = b[key]

                    if (aValue < bValue) return direction === 'asc' ? -1 : 1
                    if (aValue > bValue) return direction === 'asc' ? 1 : -1
                    return 0
                })
                return sortedList
            }
        } else {
            sortedList.sort((a, b) => {
                if (a < b) return direction === 'asc' ? -1 : 1
                if (a > b) return direction === 'asc' ? 1 : -1
                return 0
            })
            return sortedList
        }
    },
    groupBy: <T>(array: T[], key: keyof T): Map<T[keyof T], T[]> => {
        affirm(key, 'key is invalid')

        const result: Map<T[keyof T], T[]> = new Map()
        for (let i = 0; i < array.length; i++) {
            const item = array[i]
            const keyVal = item[key]

            if (result.has(keyVal)) {
                const current = result.get(keyVal)
                current.push(item)
                result.set(keyVal, current)
            } else {
                result.set(keyVal, [item])
            }
        }

        return result
    },
    chunkString: (text: string, chunkSize: number): string[] => {
        affirm(text, 'Invalid text')
        affirm(chunkSize, 'Invalid chunk size')
        affirm(text.length > 0, 'Text length must be greater than 0')
        affirm(chunkSize > 0, 'Chunk size must be greater than 0')

        if (text.length <= chunkSize) return [text]

        const numChunks = Math.ceil(text.length / chunkSize)
        const chunks = new Array(numChunks)
        for (let i = 0; i < chunks.length; i++) {
            chunks[i] = text.substring(i * chunkSize, chunkSize)
        }

        return chunks
    },
    first: <T>(arr: T[]): T => {
        affirm(algo.hasVal(arr), 'Array must not be null or undefined')
        affirm(arr.length > 0, 'Array must be non-empty')

        return arr[0]
    },
    last: <T>(arr: T[]): T => {
        affirm(algo.hasVal(arr), 'Array must not be null or undefined')
        affirm(arr.length > 0, 'Array must be non-empty')

        return arr[arr.length - 1]
    },
    mean: (numbers: number[]): number => {
        affirm(algo.hasVal(numbers), 'Array must not be null or undefined')
        affirm(numbers.length > 0, 'Array must be non-empty')

        const total = algo.sum(numbers)
        return total / numbers.length
    },
    sum: (numbers: number[]): number => {
        affirm(algo.hasVal(numbers), 'Array must not be null or undefined')
        affirm(numbers.length > 0, 'Array must be non-empty')

        let total = 0
        for (let i = 0; i < numbers.length; i++) {
            total += numbers[i]
        }
        return total
    },
    round: (number: number, precision?: number) => {
        affirm(algo.hasVal(number), 'Number must not be null or undefined')

        const factor = Math.pow(10, precision ?? 2)
        return Math.round(number * factor) / factor
    },
    min: (arr: number[]): number => {
        affirm(algo.hasVal(arr), 'Array must not be null or undefined')
        affirm(arr.length > 0, 'Array must be non-empty')

        return Math.min(...arr)
    },
    max: (arr: number[]): number => {
        affirm(algo.hasVal(arr), 'Array must not be null or undefined')
        affirm(arr.length > 0, 'Array must be non-empty')

        return Math.max(...arr)
    },
    replaceAll: (text: string, search: string, replace: string) => {
        const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special characters
        return text.replace(new RegExp(escapedSearch, 'g'), replace)
    },
    findLast: <T>(arr: T[], predicate: (value: T, index?: number, array?: T[]) => boolean): T | undefined => {
        if (!Array.isArray(arr) || arr.length === 0)
            return undefined

        for (let i = arr.length - 1; i >= 0; i--) {
            if (predicate(arr[i], i, arr))
                return arr[i]
        }
        return undefined
    },
    parsingCamelCaseText: (text: string) => {
        affirm(algo.hasVal(text), 'Text must not be null or undefined')
        affirm(text.length !== 0, 'Text must not have lenght equal to 0')
        let newText = structuredClone(text)
        newText = newText[0].toLowerCase() + newText.slice(1)
        newText = newText.trim()
        newText = algo.replaceAll(newText, ' ', '_')
        return newText
    },
    isDeepEqual: (a: any, b: any): boolean => {
        if (a === b) return true
        if (a == null || b == null) return a === b
        if (typeof a !== typeof b) return false

        if (Array.isArray(a)) {
            if (!Array.isArray(b) || a.length !== b.length) return false
            for (let i = 0; i < a.length; i++) if (!algo.isDeepEqual(a[i], b[i])) return false
            return true
        }

        if (typeof a === 'object') {
            const ka = Object.keys(a)
            const kb = Object.keys(b)
            if (ka.length !== kb.length) return false
            for (const k of ka) {
                if (!kb.includes(k)) return false
                if (!algo.isDeepEqual(a[k], b[k])) return false
            }
            return true
        }

        return false
    }
}

export default algo
